import type { UploadingAssetStatus } from "@storytell/core";
import type { UseFileUploadReturn } from "./useFileUpload";

type ScopeForUpload = {
  addUploadingAsset: (filename: string) => string;
  uploadingAssets: () => Array<{ id: string; filename: string; status: string }>;
  removeUploadingAsset: (id: string) => void;
  updateUploadingAssetStatus: (id: string, status: UploadingAssetStatus) => void;
};

interface UploadFilesWithTempAssetsParams {
  files: File[];
  fileUpload: UseFileUploadReturn;
  projectId: string;
  scope: ScopeForUpload;
  /** Called when upload is about to start */
  onUploadStart?: () => void;
}

/**
 * Upload files with temporary asset UI feedback
 * Creates temporary assets for immediate UI feedback, uploads files, and cleans up
 */
export async function uploadFilesWithTempAssets(params: UploadFilesWithTempAssetsParams): Promise<void> {
  const { files, fileUpload, projectId, scope, onUploadStart } = params;

  if (files.length > 0) {
    onUploadStart?.();
  }

  const tempAssets = files.map((file) => {
    const tempId = scope.addUploadingAsset(file.name);
    return { tempId, filename: file.name };
  });

  try {
    await fileUpload.uploadFiles(files, projectId);

    // Clean up any remaining temporary assets (in case onAssetCreated didn't fire for some files)
    tempAssets.forEach(({ tempId }) => {
      const stillExists = scope.uploadingAssets().find((asset) => asset.id === tempId);
      if (stillExists) {
        scope.removeUploadingAsset(stillExists.id);
      }
    });
  } catch (error) {
    console.error("Failed to upload files:", error);

    // Update any remaining temporary assets to error state
    tempAssets.forEach(({ tempId }) => {
      const stillExists = scope.uploadingAssets().find((asset) => asset.id === tempId);
      if (stillExists) {
        scope.updateUploadingAssetStatus(tempId, "error");
      }
    });
  }
}
